import React, { useEffect, useState } from 'react';
import Icon from '../components/Icon';
import API from '../services/api';
import { fmtLong, fmtWeekday, fmtMonthYear } from '../utils/date';
import { useLanguage } from '../context/LanguageContext';
import './Dashboard.css';

/* Günün menyusu — yalnız burada görünür, ayrıca səhifəsi yoxdur.
   Menyu olmayan günlərdə (həftəsonu, bayram) kart heç kimə göstərilmir;
   admin isə onu həmişə görür ki, yeni ayın faylını yükləyə bilsin. */
const OGUN_SIRASI = ['sehar', 'nahar', 'qelyanalti'];

function MenuCard({ t, lang, isAdmin }) {
  const [data, setData] = useState(null);
  const [yuklenir, setYuklenir] = useState(true);
  const [gonderilir, setGonderilir] = useState(false);
  const [kat, setKat] = useState('bufet');

  const yukle = React.useCallback(() => {
    setYuklenir(true);
    API.get('/menu/today', { params: { lang } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setYuklenir(false));
  }, [lang]);

  useEffect(() => { yukle(); }, [yukle]);

  const faylSec = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setGonderilir(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await API.post('/menu/upload', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(t('dashboard.menuUploaded')
        .replace('{gun}', r.data.gunSayi).replace('{yemek}', r.data.yemekSayi));
      yukle();
    } catch (err) {
      alert(err.response?.data?.message || t('common.error'));
    } finally { setGonderilir(false); }
  };

  const kateqoriyalar = data?.kateqoriyalar || {};
  const movcud = Object.keys(kateqoriyalar);
  const aktivAd = kateqoriyalar[kat] ? kat : movcud[0];
  const aktiv = aktivAd ? kateqoriyalar[aktivAd] : null;

  if (yuklenir) return null;
  if (!aktiv && !isAdmin) return null;

  return (
    <div className="dash-card dash-menu">
      <div className="dash-card-head">
        <h3 className="dash-card-title">
          <span className="ttl-emoji"><Icon name="inbox" size={17} /></span> {t('dashboard.menuTitle')}
        </h3>
        <div className="dash-menu-tools">
          {movcud.length > 1 && (
            <div className="filter-pills">
              {movcud.map(k => (
                <button key={k} onClick={() => setKat(k)}
                        className={'pill' + (aktivAd === k ? ' active' : '')}>
                  {k === 'bagca' ? t('dashboard.menuKinder') : t('dashboard.menuSchool')}
                </button>
              ))}
            </div>
          )}
          {isAdmin && (
            <label className={'dash-menu-upload' + (gonderilir ? ' busy' : '')}>
              <Icon name="upload" size={14} />
              {gonderilir ? t('dashboard.menuUploading') : t('dashboard.menuUpload')}
              <input type="file" accept=".xlsx,.xls" onChange={faylSec}
                     disabled={gonderilir} style={{ display: 'none' }} />
            </label>
          )}
        </div>
      </div>

      {!aktiv ? (
        <p className="dash-empty">{t('dashboard.menuEmpty')}</p>
      ) : (
        <div className="dash-menu-grid">
          {OGUN_SIRASI.filter(o => aktiv[o]).map(o => (
            <div key={o} className="dash-menu-col">
              <div className="dash-menu-col-head">{aktiv[o].ad}</div>
              <ul className="dash-menu-list">
                {aktiv[o].yemekler.map((y, i) => (
                  <li key={i}>
                    <span className="dash-menu-item">{y.ad}</span>
                    {y.qram && <span className="dash-menu-gram">{y.qram} q</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtMins(m) {
  if (!m && m !== 0) return '—';
  const h = Math.floor(m / 60), min = m % 60;
  return h > 0 ? (h + 'h ' + min + 'm') : (min + 'm');
}

function Stars({ val, t }) {
  if (!val) return <span className="leader-no-rating">{t('dashboard.noRating')}</span>;
  return (
    <span className="leader-stars">
      {[1, 2, 3, 4, 5].map(n => (
        <Icon key={n} name="star" size={13} filled={n <= Math.round(val)}
              style={{ color: n <= Math.round(val) ? '#f59e0b' : '#d1d5db' }} />
      ))}
      <span className="leader-stars-num">{Number(val).toFixed(1)}</span>
    </span>
  );
}

/* Reytinq sətri — kart deyil, nəticə lövhəsinin sətri.
   Solda nömrə lövhəciyi, sağda göstərici. Altdakı rəngli zolaq götürüldü:
   lider sətri onsuz da öz rəngi ilə seçilir. */
function LeaderRow({ rank, user, stat, statLabel }) {
  return (
    <div className={'lb-row' + (rank === 0 ? ' lider' : '')}>
      <span className="lb-rank">{String(rank + 1).padStart(2, '0')}</span>
      <div className="lb-who">
        <div className="lb-name">{user.name}</div>
        <div className="lb-meta">{user.position || user.role}</div>
      </div>
      <div className="lb-stat">
        <div className="lb-stat-val">{stat}</div>
        <div className="lb-stat-lbl">{statLabel}</div>
      </div>
    </div>
  );
}

function Leaderboard({ t, lang }) {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
  });

  useEffect(() => {
    API.get('/stats/leaderboard?month=' + month)
      .then(r => setData(r.data))
      .catch(() => {});
  }, [month]);

  const prevMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setMonth(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'));
  };
  const nextMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m, 1);
    const now = new Date();
    if (d > now) return;
    setMonth(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'));
  };

  const locale = lang === 'en' ? 'en-US' : 'az-AZ';
  const monthLabel = fmtMonthYear(month + '-15', lang);
  const isCurrentMonth = month === (() => {
    const now = new Date();
    return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
  })();

  return (
    <div className="dash-card dash-card-wide lb-board">
      <div className="dash-card-head">
        <h3 className="dash-card-title"><span className="ttl-emoji"><Icon name="trophy" size={17} /></span> {t('dashboard.monthlyRating')}</h3>
        <div className="leader-controls">
          {data?.totals && (
            <div className="leader-totals">
              <span>{t('dashboard.total')}: <b>{data.totals.total_tickets || 0}</b></span>
              <span>{t('dashboard.resolved')}: <b style={{ color: '#059669' }}>{data.totals.resolved_count || 0}</b></span>
              <span>{t('dashboard.open')}: <b style={{ color: '#d97706' }}>{data.totals.open_count || 0}</b></span>
              {data.totals.avg_rating && (
                <span>{t('dashboard.avgRating')}: <b style={{ color: '#f59e0b' }}><Icon name="star" size={13} filled /> {Number(data.totals.avg_rating).toFixed(1)}</b></span>
              )}
            </div>
          )}
          <button className="leader-nav" onClick={prevMonth}>‹</button>
          <span className="leader-month">{monthLabel}</span>
          <button className="leader-nav" onClick={nextMonth} disabled={isCurrentMonth}>›</button>
        </div>
      </div>

      {!data ? (
        <div className="dash-loading-text">{t('dashboard.loading')}</div>
      ) : (
        <div className="leader-cols">
          <div className="lb-col" style={{ '--lc': '#fbbf24' }}>
            <div className="lb-col-title"><Icon name="star" size={13} filled /> {t('dashboard.highestRating')}</div>
            {data.byRating.length === 0
              ? <div className="lb-empty">{t('dashboard.noData')}</div>
              : data.byRating.map((u, i) => (
                <LeaderRow key={u.id} rank={i} user={u}
                  stat={<Stars val={u.avg_rating} t={t} />}
                  statLabel={u.ticket_count + ' ' + t('dashboard.tickets')} />
              ))}
          </div>
          <div className="lb-col" style={{ '--lc': '#5eead4' }}>
            <div className="lb-col-title"><Icon name="target" size={13} /> {t('dashboard.mostTickets')}</div>
            {data.byCount.length === 0
              ? <div className="lb-empty">{t('dashboard.noData')}</div>
              : data.byCount.map((u, i) => (
                <LeaderRow key={u.id} rank={i} user={u}
                  stat={u.ticket_count + ' ' + t('dashboard.tickets')}
                  statLabel={t('dashboard.avgPrefix') + ' ' + fmtMins(u.avg_minutes)} />
              ))}
          </div>
          <div className="lb-col" style={{ '--lc': '#c4b5fd' }}>
            <div className="lb-col-title"><Icon name="zap" size={13} /> {t('dashboard.fastest')}</div>
            {data.bySpeed.length === 0
              ? <div className="lb-empty">{t('dashboard.noData')}</div>
              : data.bySpeed.map((u, i) => (
                <LeaderRow key={u.id} rank={i} user={u}
                  stat={fmtMins(u.avg_minutes)}
                  statLabel={u.ticket_count + ' ' + t('dashboard.tickets')} />
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

const REQ_STATUS_COLOR = { open:'#6b7280', assigned:'#7c3aed', in_progress:'#0369a1', resolved:'#059669', closed:'#374151' };

const ABS_LABEL_AZ = { medical:'Tibbi', family:'Ailə', personal:'Şəxsi', education:'Təhsil', other:'Digər' };
const ABS_LABEL_EN = { medical:'Medical', family:'Family', personal:'Personal', education:'Education', other:'Other' };
const PROC_TYPE_LABEL_AZ = { purchase:'Satınalma', warehouse:'Anbardan təchizat' };
const PROC_TYPE_LABEL_EN = { purchase:'Purchase', warehouse:'Warehouse' };

function mapLeaveStatus(s) {
  if (s === 'submitted') return 'open';
  if (s === 'manager_approved') return 'assigned';
  if (s === 'hr_approved') return 'in_progress';
  if (s === 'director_approved') return 'resolved';
  return 'closed';
}
function mapProcStatus(s) {
  if (s && s.startsWith('pending_')) return 'open';
  if (s === 'approved') return 'assigned';
  if (s === 'in_process') return 'in_progress';
  if (s === 'delivered' || s === 'completed') return 'resolved';
  return 'closed';
}

export default function Dashboard() {
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allTickets, setAllTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState('');
  /* Lövhədəki saat — dəqiqədə bir yenilənir, saniyə göstərilmir */
  const [saat, setSaat] = useState(() =>
    new Date().toTimeString().slice(0, 5));
  useEffect(() => {
    const id = setInterval(() => setSaat(new Date().toTimeString().slice(0, 5)), 20000);
    return () => clearInterval(id);
  }, []);

  const [attModal, setAttModal] = useState(null);
  const [attModalList, setAttModalList] = useState([]);
  const [attModalLoading, setAttModalLoading] = useState(false);

  const openAttModal = (status) => {
    setAttModal(status);
    setAttModalLoading(true);
    setAttModalList([]);
    const today = new Date().toISOString().slice(0, 10);
    API.get('/attendance', { params: { date: today } })
      .then(r => {
        const list = (r.data || []).filter(a => a.status === status);
        setAttModalList(list);
      })
      .catch(() => setAttModalList([]))
      .finally(() => setAttModalLoading(false));
  };

  const ABS_LABEL = lang === 'en' ? ABS_LABEL_EN : ABS_LABEL_AZ;
  const PROC_TYPE_LABEL = lang === 'en' ? PROC_TYPE_LABEL_EN : PROC_TYPE_LABEL_AZ;
  const locale = lang === 'en' ? 'en-US' : 'az-AZ';
  const leaveSuffix = lang === 'en' ? ' leave' : ' işburaxma';

  const STATUS_LABEL = {
    open: t('dashboard.statusOpen'),
    assigned: t('dashboard.statusAssigned'),
    in_progress: t('dashboard.statusInProgress'),
    resolved: t('dashboard.statusResolved'),
    closed: t('dashboard.statusClosed'),
  };

  useEffect(() => {
    API.get('/dashboard/stats').then(r => {
      setStats(r.data);
      const canSeeAll = r.data?.role === 'admin' || r.data?.role === 'department_head';
      if (!canSeeAll) return;
      Promise.all([
        API.get('/requests').then(rr => rr.data).catch(() => []),
        API.get('/leave').then(rr => rr.data).catch(() => []),
        API.get('/procurement').then(rr => rr.data).catch(() => []),
      ]).then(([reqs, leaves, procs]) => {
        const norm = [
          ...reqs.map(tk => ({ ...tk, _kind: 'request', _mappedStatus: tk.status })),
          ...leaves.map(l => ({
            id: 'leave-' + l.id,
            request_number: 'LEAVE-' + l.id,
            title: (ABS_LABEL[l.absence_type] || l.absence_type) + leaveSuffix,
            status: l.status,
            _mappedStatus: mapLeaveStatus(l.status),
            department_name: l.department_name || (lang === 'en' ? 'Human Resources' : 'İnsan Resursları'),
            requester_name: l.employee_name,
            assignee_name: l.manager_name,
            created_at: l.created_at,
            _kind: 'leave',
          })),
          ...procs.map(p => ({
            id: 'proc-' + p.id,
            request_number: p.request_number || ('PROC-' + p.id),
            title: (PROC_TYPE_LABEL[p.type] || p.type),
            status: p.status,
            _mappedStatus: mapProcStatus(p.status),
            department_name: p.department || (lang === 'en' ? 'Procurement' : 'Təchizat'),
            requester_name: p.requester_name,
            created_at: p.created_at,
            _kind: 'procurement',
          })),
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setAllTickets(norm);
      });
    }).finally(() => setLoading(false));
  }, [lang]);

  const filteredTickets = allTickets.filter(tk => !ticketFilter || tk._mappedStatus === ticketFilter);

  if (loading) return <div className="dash-loading"><div className="spinner"></div></div>;

  const todayPresent = stats?.todayAttendance?.find(a => a.status === 'present')?.count || 0;
  const todayAbsent = stats?.todayAttendance?.find(a => a.status === 'absent')?.count || 0;
  const isAdmin = stats?.role === 'admin';
  const req = stats?.requests || {};
  const totalAll = allTickets.length;
  /* "Açıq sorğu" = işi bitməyən sorğu. Əvvəl yalnız 'open' statusu
     sayılırdı: təyin edilmiş və icrada olan sorğular açıq sayılmırdı,
     ona görə işin qaynadığı gündə belə rəqəm 0 görünürdü. */
  const BITMIS_STATUS = ['resolved', 'closed', 'cancelled'];
  const openAll = allTickets.filter(tk => !BITMIS_STATUS.includes(tk._mappedStatus)).length;

  const FILTERS = [
    { v: '', l: t('dashboard.filterAll') },
    { v: 'open', l: t('dashboard.filterOpen') },
    { v: 'assigned', l: t('dashboard.filterAssigned') },
    { v: 'in_progress', l: t('dashboard.filterInProgress') },
    { v: 'resolved', l: t('dashboard.filterResolved') },
    { v: 'closed', l: t('dashboard.filterClosed') },
  ];

  const ATT_LABEL = {
    present: t('dashboard.present'),
    absent: t('dashboard.absent'),
    late: t('dashboard.late'),
    excused: t('dashboard.excused'),
  };

  const SECTION_LABEL = {
    preschool: t('dashboard.enrollmentPreschool'),
    primary: t('dashboard.enrollmentPrimary'),
    secondary: t('dashboard.enrollmentSecondary'),
  };

  return (
    <div className="dashboard">
      {/* Günün lövhəsi — altı ayrı kart deyil, bir bütöv.
          Rəqəmlər məktəbin gerbindəki dağ xəttinin üstündə dayanır;
          aralarını yalnız incə şaquli xətlər ayırır. Hər kartın öz
          çərçivəsi, kölgəsi və ikon qutusu olan quruluş buradan çıxıb. */}
      <div className="dash-welcome">
        <svg className="dw-ridge" viewBox="0 0 320 90" preserveAspectRatio="none" aria-hidden="true">
          <polyline points="0,84 118,12 190,66 244,28 320,76" />
          <polyline points="162,46 186,20 208,42" />
        </svg>

        <div className="dw-top">
          <div className="dash-welcome-txt">
            <h1>{t('dashboard.welcome')}{stats?.userName ? ', ' + stats.userName.split(' ')[0] : ''}!</h1>
            <p>{fmtLong(new Date(), lang)}</p>
          </div>
          <div className="dash-welcome-clock">
            <span className="dwc-time">{saat}</span>
            <span className="dwc-day">{fmtWeekday(new Date(), lang)}</span>
          </div>
        </div>

        <div className="dw-figures">
          {[
            { k: 'students', v: stats?.students || 0,                              l: t('dashboard.students'),     c: '#5eead4' },
            { k: 'teachers', v: stats?.teachers || 0,                              l: t('dashboard.teachers'),     c: '#7dd3fc' },
            { k: 'requests', v: totalAll || parseInt(req.total) || 0,              l: t('dashboard.requests'),     c: '#c4b5fd' },
            { k: 'open',     v: openAll || parseInt(req.open_count) || 0,          l: t('dashboard.openRequests'), c: '#fbbf24' },
            { k: 'present',  v: todayPresent,                                      l: t('dashboard.presentToday'), c: '#86efac' },
            { k: 'absent',   v: todayAbsent,                                       l: t('dashboard.absentToday'),  c: '#fca5a5' },
          ].map((f, i) => (
            <div key={f.k} className={'dw-fig' + (Number(f.v) === 0 ? ' sifir' : '')}
                 style={{ '--fc': f.c, animationDelay: (i * 55) + 'ms' }}>
              <span className="dw-fig-num">{f.v}</span>
              <span className="dw-fig-lbl">{f.l}</span>
            </div>
          ))}
        </div>
      </div>

      <MenuCard t={t} lang={lang} isAdmin={stats?.role === 'admin'} />

      {(stats?.pendingAdmissions > 0 || (stats?.enrollment?.pending || 0) > 0) && (
        <div className="dash-notify-row">
          {stats.pendingAdmissions > 0 && (
            <a href="/enrollment" className="dash-notify-card" style={{ '--nc': '#f59e0b' }}>
              <span className="dash-notify-icon"><Icon name="graduation" size={17} /></span>
              <span><b>{stats.pendingAdmissions}</b> {t('dashboard.pendingAdmissionsCount')}</span>
            </a>
          )}
          {(stats?.enrollment?.pending || 0) > 0 && (
            <a href="/enrollment" className="dash-notify-card" style={{ '--nc': '#0369a1' }}>
              <span className="dash-notify-icon"><Icon name="edit" size={17} /></span>
              <span><b>{stats.enrollment.pending}</b> {t('dashboard.pendingSignaturesCount')}</span>
            </a>
          )}
        </div>
      )}

      <div className="dash-grid">
        {(stats?.role === 'admin' || stats?.role === 'department_head') && (
          <div className="dash-card">
            <div className="dash-card-head">
              <h3 className="dash-card-title"><span className="ttl-emoji"><Icon name="clipboard" size={17} /></span> {t('dashboard.requestsTitle')}</h3>
              <div className="filter-pills">
                {FILTERS.map(f => (
                  <button key={f.v} onClick={() => setTicketFilter(f.v)} className={'pill' + (ticketFilter === f.v ? ' active' : '')}>{f.l}</button>
                ))}
              </div>
            </div>
            {filteredTickets.length === 0 ? <p className="dash-empty">{t('dashboard.noRequestsTitle')}</p> : (
              <div className="ticket-list">
                {filteredTickets.map(tk => {
                  const ms = tk._mappedStatus || tk.status;
                  const st = REQ_STATUS_COLOR[ms] || '#6b7280';
                  return (
                    <div key={tk.id} className="ticket-row">
                      <div className="ticket-meta">
                        <span className="ticket-num">{tk.request_number}</span>
                        <span className="ticket-status" style={{ background: st + '18', color: st }}>{STATUS_LABEL[ms] || ms}</span>
                        {tk.department_name && <span className="ticket-dept">{tk.department_name}</span>}
                      </div>
                      <div className="ticket-title">{tk.title}</div>
                      <div className="ticket-foot">
                        {tk.requester_name}{tk.assignee_name ? ' → ' + tk.assignee_name : ''} · {new Date(tk.created_at).toLocaleDateString(locale)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!isAdmin && stats?.role !== 'department_head' && (
          <div className="dash-card">
            <div className="dash-card-head">
              <h3 className="dash-card-title"><span className="ttl-emoji"><Icon name="clipboard" size={17} /></span> {t('dashboard.myRequests')}</h3>
              <a href="/my-requests" className="dash-link">{t('dashboard.viewAll')} →</a>
            </div>
            {(!stats?.myRecent || stats.myRecent.length === 0) ? (
              <div className="dash-empty">{t('dashboard.noRequests')}</div>
            ) : (
              <div className="ticket-list">
                {stats.myRecent.map(tk => {
                  const stColor = tk.status === 'resolved' || tk.status === 'closed' ? '#16a34a'
                    : tk.status === 'in_progress' || tk.status === 'assigned' ? '#d97706'
                    : tk.status === 'open' ? '#dc2626' : '#6b7280';
                  const stLabel = STATUS_LABEL[tk.status] || tk.status;
                  return (
                    <a key={tk.id} href="/my-requests" className="my-ticket-row">
                      <span className="ticket-num">{tk.request_number}</span>
                      <span className="ticket-status" style={{ background: stColor + '15', color: stColor }}>{stLabel}</span>
                      <span className="ticket-dept">{tk.department_name || '—'}</span>
                      <span className="ticket-title-inline">{tk.title}</span>
                      <span className="ticket-date">{new Date(tk.created_at).toLocaleDateString(locale)}</span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {stats?.enrollment && (
          <div className="dash-card">
            <h3 className="dash-card-title"><span className="ttl-emoji"><Icon name="graduation" size={17} /></span> {t('dashboard.enrollment')}</h3>
            <div className="enroll-grid">
              {(stats.enrollment.bySection || []).map(s => (
                <div key={s.section} className="enroll-cell">
                  <div className="enroll-num">{s.count}</div>
                  <div className="enroll-label">{SECTION_LABEL[s.section] || s.section}</div>
                </div>
              ))}
            </div>
            <div className="enroll-summary">
              <span>{t('dashboard.total')}: <b>{stats.enrollment.total}</b></span>
              <span>{t('dashboard.signed')}: <b style={{ color: '#059669' }}>{stats.enrollment.signed}</b></span>
              <span>{t('dashboard.pending')}: <b style={{ color: '#d97706' }}>{stats.enrollment.pending}</b></span>
            </div>
          </div>
        )}

        <div className="dash-card">
          <h3 className="dash-card-title"><span className="ttl-emoji"><Icon name="megaphone" size={17} /></span> {t('dashboard.announcements')}</h3>
          {stats?.announcements?.length === 0
            ? <p className="dash-empty">{t('dashboard.noAnnouncements')}</p>
            : stats?.announcements?.map(a => (
              <div key={a.id} className="announcement-item">
                <div className="ann-title">{a.title}</div>
                <div className="ann-meta">{a.author_name} · {new Date(a.created_at).toLocaleDateString(locale)}</div>
              </div>
            ))
          }
        </div>

        <div className="dash-card">
          <h3 className="dash-card-title"><span className="ttl-emoji"><Icon name="chartBar" size={17} /></span> {t('dashboard.todayAttendance')}</h3>
          {(!stats?.todayAttendance || stats.todayAttendance.length === 0)
            ? <p className="dash-empty">{t('dashboard.noAttendanceToday')}</p>
            : stats.todayAttendance.map(a => {
              const clickable = a.status !== 'present' && a.count > 0;
              return (
                <div
                  key={a.status}
                  className={'att-item' + (clickable ? ' att-item-clickable' : '')}
                  onClick={clickable ? () => openAttModal(a.status) : undefined}
                  style={clickable ? { cursor: 'pointer' } : undefined}
                >
                  <span className={'att-badge att-' + a.status}>{ATT_LABEL[a.status] || a.status}</span>
                  <span className="att-count">
                    {a.count} {t('dashboard.people')}
                    {clickable && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ marginLeft: 6, verticalAlign: 'middle' }}>
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    )}
                  </span>
                </div>
              );
            })
          }
        </div>

        {attModal && (
          <div className="dash-att-modal-overlay" onClick={() => setAttModal(null)}>
            <div className="dash-att-modal" onClick={e => e.stopPropagation()}>
              <div className="dash-att-modal-header">
                <span className={'att-badge att-' + attModal}>{ATT_LABEL[attModal] || attModal}</span>
                <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '0.85rem' }}>
                  {new Date().toLocaleDateString(locale)}
                </span>
                <button onClick={() => setAttModal(null)} style={{ background:'none', border:'none', cursor:'pointer', marginLeft: 12, color:'#64748b' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div className="dash-att-modal-body">
                {attModalLoading ? (
                  <div style={{ textAlign:'center', padding: 30, color:'#94a3b8' }}>{t('dashboard.loading')}</div>
                ) : attModalList.length === 0 ? (
                  <div style={{ textAlign:'center', padding: 30, color:'#94a3b8' }}>—</div>
                ) : (
                  <table className="dash-att-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('students.colName')}</th>
                        <th>{t('students.colClass') || t('attendance.class')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attModalList.map((a, i) => (
                        <tr key={a.id || i}>
                          <td>{i + 1}</td>
                          <td>{a.student_name}</td>
                          <td>{a.class_name || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        <Leaderboard t={t} lang={lang} />
      </div>
    </div>
  );
}
